import { useInfiniteQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface MiningLogSummary {
  logId: string;
  userId: string;
  landId: string;
  landType: string;
  blocksMined: number;
  itemsMinted: Array<{ itemName: string; quantity: number }>;
  createdAt: string;
}

interface UseMiningLogReturn {
  entries: MiningLogSummary[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

const LIMIT = 50;

export function useMiningLog(): UseMiningLogReturn {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: queryKeys.mining.log(),
      queryFn: async ({ pageParam = 0 }) => {
        const params: Record<string, string> = {
          limit: String(LIMIT),
          offset: String(pageParam),
        };

        const { data } = await api.get<{ entries: MiningLogSummary[]; total: number }>(
          '/arches/mining/log',
          { params },
        );
        return data;
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const next = allPages.length * LIMIT;
        return next < lastPage.total ? next : undefined;
      },
    });

  const entries = data?.pages.flatMap((p) => p.entries) ?? [];
  const total = data?.pages[0]?.total ?? 0;

  return {
    entries,
    total,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load mining log' : null,
    refresh: () => {
      refetch();
    },
    loadMore: () => {
      if (hasNextPage && !isFetchingNextPage) fetchNextPage();
    },
    hasMore: !!hasNextPage,
  };
}
